import React from 'react';

import { Box, Typography } from '@mui/material';
import { PatientActivity } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { usePatientActivityQuery } from '@uplift/queries';
import { CalendarDateService, DateFormats } from '@uplift/services';
import { Loader, useOnScreenObserver } from '@uplift/ui-v2';

import { ActivityPage } from './ActivityPage.component';
import { AddActivityButton } from './AddActivityButton.component';
import { ActivitiesFilters, ActivitiesFiltersContext } from './ActivitiesFilters.component';


const ActivityListContent: React.VFC<{
  patientId: string;
  onActivitySelect?: (item: PatientActivity) => void;
}> = ({ patientId, onActivitySelect }) => {
  const timezone = useTimezone();
  const filtersContext = ActivitiesFiltersContext.useContext();
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = usePatientActivityQuery(patientId, {
    ...filtersContext.filters,
    fromDate: filtersContext.filters.fromDate ? filtersContext.filters.fromDate.format(DateFormats.date) : null,
    toDate: filtersContext.filters.toDate ? filtersContext.filters.toDate.format(DateFormats.date) : null,
  }, { suspense: true });

  const now = React.useMemo(() => CalendarDateService.date().tz(timezone), [ timezone ]);
  const loaderRef = useOnScreenObserver(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  });

  const hasSomeActivities = React.useMemo(
    () => data?.pages?.[0]?.data?.length > 0,
    [ data ]
  );

  const content = React.useMemo(() => {
    if (!hasSomeActivities) {
      return (
        <Typography component="i" color="textSecondary">No activity yet.</Typography>
      );
    }

    return data?.pages?.map((page, index, arr) => {
      return page.data.length > 0 && (
        <ActivityPage
          key={page.cursor}
          now={now}
          prevActivities={arr[index - 1]?.data}
          activities={page.data}
          onItemSelect={onActivitySelect}
        />
      );
    });
  }, [ data?.pages, hasSomeActivities, now, onActivitySelect ]);

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      {content}
      {hasNextPage && (
        <div
          ref={loaderRef}
          key={isFetchingNextPage ? 'loading' : 'load-more'}
        >
          <Loader color="secondary" size="2rem" mt={3} />
        </div>
      )}
    </Box>
  );
};


export const ActivityList: React.VFC<{
  patientId: string;
  onActivitySelect?: (item: PatientActivity) => void;
  onUploadDocumentOpen?: () => void;
}> = (props) => {
  return (
    <Box>
      <Box mb={3} display="flex" gap={1}>
        <Typography variant="h5" fontWeight={450}>Activity</Typography>
        <Box flex={1} />
        <ActivitiesFilters />
        <AddActivityButton
          patientId={props.patientId}
          onUploadDocumentOpen={props.onUploadDocumentOpen}
        />
      </Box>
      <React.Suspense fallback={<Loader color="secondary" size="2rem" mt={10} />}>
        <ActivityListContent {...props} />
      </React.Suspense>
    </Box>
  );
};
